// YardPerformance.js — TAM Central Station "Performance" section, in JarJar.
// Data: YMSEV_<site>_<UTC hour> blocks + YMSPERF_<site>_BOARD (yms-perf-sync.sh).
// Logic: TCS's own pairing / table builders (./yardperf/tcs, ported verbatim).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { computePairedMoves } from './yardperf/tcs/pairing';
import { timestampsToHeatmapRows, completeEventsToDriverHeatmapRows } from './yardperf/tcs/HeatmapTable';
import { buildTimingRows, buildDockTimingRows, ANDON_SUFFIXES } from './yardperf/tcs/TimingTable';
import { bucketDriverRows, bucketMoveRows, bucketCompleteRows, bucketAndonRows } from './yardperf/tcs/heatmapCellEvents';
import { resolveTimingCell, resolveDockCell } from './yardperf/tcs/timingCellEvents';
import { HeatmapGridCard, TimingTableCard, DockTimingTableCard, TdrDeptCard, TopImpactGrid, DriverBreakdownView, MoveHistoryView, Card, CELL_HOVER_CSS } from './yardperf/cards';

const TABS = [
  { key: 'inyard', label: 'In-yard' },
  { key: 'offsite', label: 'Offsite' },
  { key: 'andons', label: 'Andons' },
  { key: 'topImpact', label: 'Top Impact' },
  { key: 'driverBreakdown', label: 'Transaction Breakdown', restricted: true },
  { key: 'moveHistory', label: 'Move History' },
];
// OLM1 shift presets (whole hours, like TCS): day 08:00-18:30, night 19:00-05:30.
const PRESETS = { day: { start: 8, end: 19 }, night: { start: 19, end: 6 } };

const pad = n => String(n).padStart(2, '0');
const hourKey = (site, epochSec) => {
  const d = new Date(epochSec * 1000);
  return `YMSEV_${site}_${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}${pad(d.getUTCHours())}`;
};
function defaultShift() {
  const now = new Date();
  const h = now.getHours();
  const d = new Date(now);
  if (h < 6) d.setDate(d.getDate() - 1);
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return (h >= 19 || h < 6) ? { date, ...PRESETS.night } : { date, ...PRESETS.day };
}
function windowFor(date, startHour, endHour) {
  const [y, m, d] = date.split('-').map(Number);
  const fromDate = new Date(y, m - 1, d, startHour).getTime();
  const toDate = endHour > startHour ? new Date(y, m - 1, d, endHour).getTime() : new Date(y, m - 1, d + 1, endHour).getTime();
  return { fromDate, toDate };
}

export default function YardPerformance({ api, site, canSeeDrivers }) {
  const init = useMemo(defaultShift, []);
  const [date, setDate] = useState(init.date);
  const [startHour, setStartHour] = useState(init.start);
  const [endHour, setEndHour] = useState(init.end);
  const [tab, setTab] = useState('inyard');
  const [perf, setPerf] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (site !== 'OLM1') return;
    setLoading(true); setStatus('Loading YMS events…');
    try {
      const { fromDate, toDate } = windowFor(date, startHour, endHour);
      const firstHour = Math.floor((fromDate - 6 * 3600000) / 3600000) * 3600;
      const lastHour = Math.floor((toDate + 2 * 3600000 - 1) / 3600000) * 3600;
      const hours = [];
      for (let h = firstHour; h <= lastHour; h += 3600) hours.push(h);
      const get = key => fetch(`${api}/api/fclm/cache?site=${site}&date=${key}&shift_type=data`).then(r => (r.ok ? r.json() : null)).catch(() => null);
      const [board, ...blocks] = await Promise.all([get(`YMSPERF_${site}_BOARD`), ...hours.map(h => get(hourKey(site, h)))]);
      const missing = blocks.filter(b => !b || !Array.isArray(b.events)).length;
      const all = blocks.flatMap(b => (b && Array.isArray(b.events) ? b.events : []));
      const padFrom = fromDate - 2 * 3600000, padTo = toDate + 2 * 3600000, lookFrom = fromDate - 6 * 3600000;
      const events = all.filter(e => e.timestamp * 1000 >= padFrom && e.timestamp * 1000 < padTo);
      const lookbackEvents = all.filter(e => e.timestamp * 1000 >= lookFrom && e.timestamp * 1000 < padFrom);
      const data = computePairedMoves({ yard: site, dateVal: date, startHour, endHour, fromDate, toDate, events, lookbackEvents, board: board && board.locationTypeMap ? board : null });
      setPerf(data);
      const andonCount = data.andonEvents.filter(e => ANDON_SUFFIXES.some(s => e.eventType === `COMPLETE_${s}`)).length;
      const latest = blocks.reduce((m, b) => Math.max(m, (b && b.fetchedAt) || 0), 0);
      setStatus(`${data.createEvents.length} creates, ${data.completeEvents.length} completes, ${andonCount} andons`
        + (latest ? ` · YMS synced ${new Date(latest * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '')
        + (missing ? ` · ${missing} of ${hours.length} hours not synced yet` : ''));
    } catch (e) {
      setStatus(`Error: ${e.message}`);
    }
    setLoading(false);
  }, [api, site, date, startHour, endHour]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 5 * 60 * 1000); return () => clearInterval(t); }, [load]);

  // Mirrors HostlerPerformanceView's usePerformanceData assembly.
  const view = useMemo(() => {
    if (!perf) return null;
    const { createEvents, completeEvents, dockEvents, andonEvents, andonLifecycles, onsitePairs, offsitePairs, pairedMoves, priorPairs,
      isOffsite, shuttleCreateIds, fromDate, toDate, startHour: sh, numHours, fetchedAt } = perf;
    const nh = numHours ?? 24;
    const through = Math.min(toDate ?? Infinity, fetchedAt ?? Date.now());
    const shuttleCompleteIds = new Set(offsitePairs.map(p => p.complete?.eventId).filter(Boolean));
    const onsiteCompleteIds = new Set(onsitePairs.map(p => p.complete?.eventId).filter(Boolean));
    const isShuttleCreate = e => shuttleCreateIds.has(e.eventId);
    const isShuttleComplete = e => shuttleCompleteIds.has(e.eventId) || (!onsiteCompleteIds.has(e.eventId) && isOffsite(e.location));
    const onsiteCreates = createEvents.filter(e => !isShuttleCreate(e));
    const onsiteCompletes = completeEvents.filter(e => !isShuttleComplete(e));
    const shuttleCreates = createEvents.filter(isShuttleCreate);
    const shuttleCompletes = completeEvents.filter(isShuttleComplete);
    const andonCreates = andonEvents.filter(e => ANDON_SUFFIXES.some(s => e.eventType === `CREATE_${s}`));
    const andonCompletes = andonEvents.filter(e => ANDON_SUFFIXES.some(s => e.eventType === `COMPLETE_${s}`));
    const H = ts => timestampsToHeatmapRows(ts.map(e => e.timestamp), sh, fromDate, nh, through);
    const completePairByEventId = new Map();
    for (const p of pairedMoves) if (p.complete) completePairByEventId.set(p.complete.eventId, p);
    return {
      inyard: {
        create: H(onsiteCreates), complete: H(onsiteCompletes), drivers: completeEventsToDriverHeatmapRows(onsiteCompletes, sh, fromDate, nh),
        timingRows: buildTimingRows(onsitePairs, andonEvents, sh, fromDate, nh, andonLifecycles),
        dockRows: buildDockTimingRows(onsitePairs, dockEvents, sh, fromDate, [...(priorPairs || []), ...pairedMoves.filter(p => !onsitePairs.includes(p))], nh),
      },
      offsite: { create: H(shuttleCreates), complete: H(shuttleCompletes), drivers: completeEventsToDriverHeatmapRows(shuttleCompletes, sh, fromDate, nh) },
      andons: { create: H(andonCreates), complete: H(andonCompletes) },
      bucketing: {
        fromDate, numHours: nh, moveDetailByCreateId: perf.moveDetailByCreateId, completePairByEventId, andonEvents,
        series: { inyardCreate: onsiteCreates, inyardComplete: onsiteCompletes, inyardDrivers: onsiteCompletes, offsiteCreate: shuttleCreates, offsiteComplete: shuttleCompletes, offsiteDrivers: shuttleCompletes },
      },
    };
  }, [perf]);

  const resolver = useCallback(seriesKey => cell => {
    if (!view) return [];
    const { fromDate, numHours, series, moveDetailByCreateId, completePairByEventId, andonEvents } = view.bucketing;
    const ev = series[seriesKey] || [];
    const { hourIndex, quarter } = cell;
    if (seriesKey.endsWith('Drivers')) return bucketDriverRows(ev, hourIndex, quarter, fromDate, numHours);
    if (seriesKey.endsWith('Create') && seriesKey !== 'andonCreate') return bucketMoveRows(ev, moveDetailByCreateId, hourIndex, quarter, fromDate, numHours);
    if (seriesKey.endsWith('Complete') && seriesKey !== 'andonComplete') return bucketCompleteRows(ev, completePairByEventId, hourIndex, quarter, fromDate, numHours);
    if (seriesKey === 'andonCreate') return bucketAndonRows(andonEvents, 'CREATE', hourIndex, quarter, fromDate, numHours, ANDON_SUFFIXES);
    return bucketAndonRows(andonEvents, 'COMPLETE', hourIndex, quarter, fromDate, numHours, ANDON_SUFFIXES);
  }, [view]);

  if (site !== 'OLM1') {
    return <div className="card"><p style={{ fontSize: '0.85rem' }}>Yard Performance is available for OLM1 only right now.</p></div>;
  }
  const visibleTabs = TABS.filter(t => !t.restricted || canSeeDrivers);
  const activeTab = visibleTabs.some(t => t.key === tab) ? tab : 'inyard';
  const btn = active => ({ padding: '6px 14px', border: 'none', borderBottom: active ? '3px solid #077398' : '3px solid transparent', background: 'none', fontWeight: active ? 700 : 500, cursor: 'pointer', fontSize: 14 });
  const hourOpts = Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{pad(h)}:00</option>);

  return (
    <div>
      <style>{CELL_HOVER_CSS}</style>
      <h2 className="section-title">Yard Performance — {site}</h2>
      <Card title={`Timeframe — ${site}`} style={{ display: 'block', margin: '0 0 16px' }}
        accessory={<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#697576' }}>{status}</span>
          <button className="btn" onClick={load} disabled={loading} style={{ background: '#077398', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4 }}>{loading ? 'Fetching…' : 'Fetch'}</button>
        </div>}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12 }}>Date<br /><input type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
          <label style={{ fontSize: 12 }}>From<br /><select value={startHour} onChange={e => setStartHour(Number(e.target.value))}>{hourOpts}</select></label>
          <label style={{ fontSize: 12 }}>To<br /><select value={endHour} onChange={e => setEndHour(Number(e.target.value))}>{hourOpts}</select></label>
          <button className="btn" onClick={() => { setStartHour(PRESETS.day.start); setEndHour(PRESETS.day.end); }}>☀️ Days</button>
          <button className="btn" onClick={() => { setStartHour(PRESETS.night.start); setEndHour(PRESETS.night.end); }}>🌙 Nights</button>
        </div>
      </Card>
      <div role="tablist" aria-label="Performance views" style={{ display: 'flex', borderBottom: '1px solid #E7E9E9', marginBottom: 16 }}>
        {visibleTabs.map(t => <button key={t.key} role="tab" aria-selected={activeTab === t.key} style={btn(activeTab === t.key)} onClick={() => setTab(t.key)}>{t.label}</button>)}
      </div>
      {!view ? <p style={{ fontSize: 13, color: '#697576' }}>{loading ? 'Loading…' : 'No data.'}</p> : (
        <div>
          {activeTab === 'inyard' && <>
            <HeatmapGridCard title="Move Create Time" series="inyardCreate" rows={view.inyard.create} resolveCellEvents={resolver('inyardCreate')} />
            <HeatmapGridCard title="Move Complete Time" series="inyardComplete" rows={view.inyard.complete} resolveCellEvents={resolver('inyardComplete')} />
            <HeatmapGridCard title="Active Drivers" series="inyardDrivers" rows={view.inyard.drivers} colorScale="green" dataBar={false} summaryType="average" resolveCellEvents={resolver('inyardDrivers')} />
            <TimingTableCard title="Timing Averages" rows={view.inyard.timingRows} resolveCellEvents={c => resolveTimingCell(view.inyard.timingRows, c.hourIndex, c.metricField)} />
            <DockTimingTableCard title="Dock Timing" rows={view.inyard.dockRows} resolveCellEvents={c => resolveDockCell(view.inyard.dockRows, c.hourIndex, c.metricField)} />
            <TdrDeptCard title="TDR Rel→Create by Dept" dockRows={view.inyard.dockRows} site={site} />
          </>}
          {activeTab === 'offsite' && <>
            <HeatmapGridCard title="Shuttle Move Create" series="offsiteCreate" rows={view.offsite.create} resolveCellEvents={resolver('offsiteCreate')} />
            <HeatmapGridCard title="Shuttle Move Complete" series="offsiteComplete" rows={view.offsite.complete} resolveCellEvents={resolver('offsiteComplete')} />
            <HeatmapGridCard title="Active Drivers" series="offsiteDrivers" rows={view.offsite.drivers} colorScale="green" dataBar={false} summaryType="average" resolveCellEvents={resolver('offsiteDrivers')} />
          </>}
          {activeTab === 'andons' && <>
            <HeatmapGridCard title="Andon Create" series="andonCreate" rows={view.andons.create} resolveCellEvents={resolver('andonCreate')} />
            <HeatmapGridCard title="Andon Complete" series="andonComplete" rows={view.andons.complete} resolveCellEvents={resolver('andonComplete')} />
          </>}
          {activeTab === 'topImpact' && <TopImpactGrid onsitePairs={perf.onsitePairs} offsitePairs={perf.offsitePairs} />}
          {activeTab === 'driverBreakdown' && canSeeDrivers && <DriverBreakdownView perfData={perf} />}
          {activeTab === 'moveHistory' && <MoveHistoryView pairs={[...perf.onsitePairs, ...perf.offsitePairs]} />}
        </div>
      )}
    </div>
  );
}
